<!-- nuc/snitch status component for the dashboard landing -->
<template>
  <div class="card dashboard-card dashboard-card--snitch">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div class="snitch-header-text">
        <div class="snitch-title-row">
          <h4 class="mb-0">NUC Status</h4>

          <info-box class="snitch-info" :leftOffset="-10">
            <p>
              A NUC is a device used to host the Sensoreal Touchscreen
              experience. The table shown here gives an overview of the key
              status updates of each device associated with the project.
            </p>
          </info-box>
        </div>

        <small class="text-muted">Live device health</small>
      </div>

      <!-- open snitch button removed - maybe show for user specific permissions -->
      <!--
      <button
        class="btn btn-sm btn-outline-primary"
        @click="openSnitch"
        title="Open full Snitch"
      >
        Open Snitch
      </button>
      -->
    </div>

    <div class="card-body">
      <div v-if="loading" class="text-muted">Loading device status…</div>
      <div v-else-if="!rows.length" class="text-muted">
        No devices found for this project.
      </div>

      <v-table
        v-else
        :columns="columns"
        :rows="rows"
        :show-active-toggle="false"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VTable from "@/components/VTable";
import InfoBox from "@/components/InfoBox";

const SNITCH_API = "https://snitch.sensoreal.com.au/api/snitches";
const SNITCH_UI = "https://snitch.sensoreal.com.au";

export default {
  name: "DashboardSnitchStatus",
  components: { VTable, InfoBox },

  data() {
    return {
      snitches: {},
      loading: true,
      poller: null,
    };
  },

  computed: {
    // for nuc device filtering
    projectKey() {
      const cfg = this.$config || {};
      const name = cfg.snitch.devicePrefix || cfg.projectName || "";

      return name
        .toLowerCase()
        .replace(/\s+/g, "") // remove spaces
        .replace(/[^a-z0-9]/g, "") // remove punctuation
        .trim();
    },

    normalizedSnitches() {
      const out = {};
      Object.keys(this.snitches || {}).forEach((name) => {
        const key = name.toLowerCase().replace(".local", "").trim();
        out[key] = out[key] || {};
        Object.assign(out[key], this.snitches[name]);
      });
      return out;
    },

    rows() {
      const keys = Object.keys(this.normalizedSnitches).sort();

      const norm = (s) =>
        (s || "")
          .toLowerCase()
          .replace(".local", "")
          .replace(/\s+/g, "")
          .replace(/[^a-z0-9]/g, "")
          .trim();

      const filtered = this.projectKey
        ? keys.filter((k) => norm(k).startsWith(this.projectKey))
        : keys;

      return filtered.map((device) => {
        const d = this.normalizedSnitches[device] || {};
        return {
          device,
          last_seen: d.last_seen,
          touch_seen: d.touch_seen,
          last_sync_attempt: d.last_sync_attempt,
          last_sync_success: d.last_sync_success,
          last_boot: d.last_boot,
        };
      });
    },

    columns() {
      const makeCell = (ms, statusFn) => {
        const emoji = statusFn(ms);
        const text = this.parseTimestamp(ms) || "-";
        return `<span class="emoji">${emoji}</span> ${text}`;
      };

      return [
        { label: "Device", field: "device", options: { sortable: true } },
        {
          label: "Last Device Seen",
          field: "last_seen",
          options: {
            sortable: true,
            return: (ms) => makeCell(ms, this.getSeenStatus),
          },
        },
        {
          label: "Last Sync Attempt",
          field: "last_sync_attempt",
          options: {
            sortable: true,
            return: (ms) => makeCell(ms, this.getSyncAttemptStatus),
          },
        },
        {
          label: "Last Sync Success",
          field: "last_sync_success",
          options: {
            sortable: true,
            return: (ms) => makeCell(ms, this.getSyncSuccessStatus),
          },
        },
        {
          label: "Last Boot",
          field: "last_boot",
          options: {
            sortable: true,
            return: (ms) => makeCell(ms, this.getBootStatus),
          },
        },
      ];
    },
  },

  mounted() {
    this.fetchSnitches();
    this.poller = setInterval(this.fetchSnitches, 10000);
  },

  beforeDestroy() {
    clearInterval(this.poller);
  },

  methods: {
    fetchSnitches() {
      axios
        .get(SNITCH_API)
        .then((res) => {
          this.snitches = res.data || {};
        })
        .finally(() => {
          this.loading = false;
        });
    },

    openSnitch() {
      window.open(SNITCH_UI, "_blank", "noopener,noreferrer");
    },

    getSeenStatus(ms) {
      return this.statusByMinutes(ms, 60 * 24, 1);
    },
    getSyncAttemptStatus(ms) {
      return this.statusByMinutes(ms, 10, 5);
    },
    getSyncSuccessStatus(ms) {
      return this.statusByMinutes(ms, 60 * 24 * 14, 60 * 24 * 7);
    },
    getBootStatus(ms) {
      return this.statusByMinutes(ms, 60 * 24, 0);
    },
    statusByMinutes(ms, danger, warn) {
      if (!ms || typeof ms !== "number" || isNaN(ms)) return "";
      const diff = (Date.now() - ms) / 1000 / 60;
      if (diff > danger) return "🚨";
      if (diff > warn) return "⚠️";
      return "✅";
    },
    parseTimestamp(ms) {
      if (!ms || typeof ms !== "number" || isNaN(ms)) return "-";

      const minutes = Math.round((Date.now() - ms) / 1000 / 60);

      if (!isFinite(minutes) || minutes < 0) return "-";
      if (minutes < 1) return "< 1m ago";
      if (minutes < 60) return `${minutes}m ago`;
      if (minutes < 1440)
        return `${Math.floor(minutes / 60)}hr ${minutes % 60}m ago`;

      return `${Math.floor(minutes / 1440)}d ${Math.floor(
        (minutes % 1440) / 60
      )}hr`;
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard-card {
  height: 100%;
}

/* hide VTable pagination in dashboard context */
.dashboard-card :deep(.pagination) {
  display: none !important;
}

.dashboard-card--snitch :deep(tbody td:first-child) {
  font-weight: 700;
}

.snitch-header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Prevent wide table from overflowing the card */
.dashboard-card--snitch :deep(.table-wrapper) {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Ensure the actual table has room; allows horizontal scroll on narrow cards */
.dashboard-card--snitch :deep(table) {
  width: 100%;
  min-width: 900px; /* tweak if needed */
  table-layout: auto;
}

/* keep header labels from forcing awkward wraps */
.dashboard-card--snitch :deep(th) {
  white-space: nowrap;
}

/* align emojis in vtable */
.emoji {
  display: inline-block;
  width: 18px;
  text-align: center;
  margin-right: 6px;
}
</style>

<!-- global overrides  -->
<style lang="scss">
.dashboard-card .pagination,
.dashboard-card tfoot {
  display: none !important;
}

.dashboard-card--snitch .table-wrapper {
  margin-top: 0px !important;
}

.dashboard-card--snitch .table-wrapper tbody td:first-child {
  font-weight: 700;
}

/* ---- InfoBox alignment fixes (global, works across component boundary) ---- */
.snitch-title-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

.snitch-info {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.snitch-info .info-box__btn {
  position: relative;
  top: 2px;
}

/* Optional: match header tone (uncomment if desired)
.snitch-info .info-box {
  color: #6b7280;
}

.snitch-info .info-box:hover,
.snitch-info .info-box--active {
  color: #0d6efd;
}
*/

/* ---- Fix: keep VTable contained inside the Snitch card ---- */
.dashboard-card--snitch .table-wrapper {
  max-width: 100% !important;
  overflow-x: auto !important;
  overflow-y: hidden !important;
  -webkit-overflow-scrolling: touch;
}

/* Ensure the table can be wider than the card (so it scrolls instead of spilling) */
.dashboard-card--snitch .table-wrapper table {
  width: 100% !important;
  min-width: 900px; /* tweak 800–1100 depending on your columns */
  table-layout: auto !important;
}

/* Prevent long headers forcing weird wraps */
.dashboard-card--snitch .table-wrapper th {
  white-space: nowrap;
}
</style>
